import type { LoginFormProps } from '../../../../types/authForms';
import { useState } from 'react';
import { Controller } from 'react-hook-form';
import { Box, Stack, TextField, InputAdornment, IconButton, Checkbox, FormControlLabel, Button } from '@mui/material';
import { StyleObj } from '../../../../@types';
import { useLoginForm } from '../../../../hooks/useLoginForm';
import { LOGIN_MODE } from '../../../../types/enums';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const COLORS = {
  background: '#DDDDDD',
  inputBackground: '#E8F4FD',
  buttonBackground: '#FFCC00',
  buttonText: '#000000',
  text: '#333333',
  border: '#CCCCCC',
  icon: '#999999',
} as const;

const styles: StyleObj = {
  container: {
    width: '100%',
    height: '100%',
    mx: 'auto',
    px: 3,
    pt: 2,
    pb: 8,
    backgroundColor: COLORS.background,
    borderRadius: 0,
  },
  input: {
    backgroundColor: COLORS.inputBackground,
    borderRadius: 1,
    border: '1px solid',
    borderColor: COLORS.border,
    mb: 1,
    '& .MuiOutlinedInput-root': {
      backgroundColor: COLORS.inputBackground,
      borderRadius: 1,
      pr: 0,
      '& fieldset': {
        borderColor: COLORS.border,
      },
      '&:hover fieldset': {
        borderColor: COLORS.border,
      },
      '&.Mui-focused fieldset': {
        borderColor: COLORS.border,
      },
    },
    '& .MuiInputBase-input': {
      py: 1.5,
      px: 2,
      color: COLORS.text,
      '&:-webkit-autofill': {
        WebkitBoxShadow: `0 0 0 1000px ${COLORS.inputBackground} inset !important`,
        WebkitTextFillColor: `${COLORS.text} !important`,
        caretColor: COLORS.text,
      },
      '&:-webkit-autofill:hover': {
        WebkitBoxShadow: `0 0 0 1000px ${COLORS.inputBackground} inset !important`,
      },
      '&:-webkit-autofill:focus': {
        WebkitBoxShadow: `0 0 0 1000px ${COLORS.inputBackground} inset !important`,
      },
      '&:-webkit-autofill:active': {
        WebkitBoxShadow: `0 0 0 1000px ${COLORS.inputBackground} inset !important`,
      },
    },
  },
  inputAdornment: {
    backgroundColor: COLORS.background,
    height: 46,
    maxHeight: 'none',
    color: COLORS.icon,
    px: 1.5,
  },
  inputAdornmentIconButton: {
    color: COLORS.icon,
  },
  header: {
    display: 'flex',
    alignItems: 'flex-end',
    mb: 4,
  },
  closeButton: {
    color: COLORS.icon,
    p: 0.5,
  },
  savePasswordWrapper: {
    display: 'flex',
    alignItems: 'center',
    mb: 1,
  },
  checkbox: {
    color: COLORS.border,
    '&.Mui-checked': {
      color: COLORS.buttonBackground,
    },
  },
  savePasswordLabel: {
    color: COLORS.icon,
    fontSize: 14,
  },
  submitButton: {
    width: '100%',
    height: 54,
    py: 1.5,
    backgroundColor: COLORS.buttonBackground,
    color: COLORS.buttonText,
    border: 'none',
    borderRadius: 0,
    fontSize: 16,
    fontWeight: 400,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#E6B800',
    },
    '&:disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
  },
  footer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 1,
    mt: 3,
  },
  footerText: {
    fontSize: 14,
    color: COLORS.text,
  },
  footerLink: {
    minWidth: 'auto',
    p: 0,
    fontSize: 14,
    color: COLORS.text,
    textDecoration: 'underline',
    textTransform: 'none',
    '&:hover': {
      textDecoration: 'underline',
      color: '#000000',
    },
  },
};

const GolaLoginForm = ({ presentation = 'modal' }: LoginFormProps) => {
  const { t, control, errors, isSubmitting, handleClose, onSubmit } = useLoginForm({
    mode: LOGIN_MODE.USERNAME,
    presentation,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [savePasswordChecked, setSavePasswordChecked] = useState(false);

  const toggleShowPassword = () => setShowPassword(!showPassword);

  return (
    <Box sx={styles.container}>
      {handleClose && (
        <Stack sx={styles.header}>
          <IconButton onClick={handleClose} sx={styles.closeButton}>
            <CloseIcon />
          </IconButton>
        </Stack>
      )}
      <form onSubmit={onSubmit}>
        <Controller
          name='username'
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              placeholder={t('register.username')}
              variant='outlined'
              sx={styles.input}
              error={!!errors.username}
              helperText={errors.username?.message}
              disabled={isSubmitting}
              slotProps={{
                input: {
                  endAdornment: field.value ? (
                    <InputAdornment position='end' sx={styles.inputAdornment}>
                      <IconButton
                        onClick={() => field.onChange('')}
                        edge='end'
                        size='small'
                        sx={styles.inputAdornmentIconButton}
                      >
                        <CloseIcon fontSize='small' />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                },
              }}
            />
          )}
        />

        <Controller
          name='password'
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              type={showPassword ? 'text' : 'password'}
              placeholder={t('register.password')}
              variant='outlined'
              sx={styles.input}
              error={!!errors.password}
              helperText={errors.password?.message}
              disabled={isSubmitting}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position='end' sx={styles.inputAdornment}>
                      <IconButton
                        onClick={toggleShowPassword}
                        edge='end'
                        size='small'
                        sx={styles.inputAdornmentIconButton}
                      >
                        {showPassword ? <VisibilityOffIcon fontSize='small' /> : <VisibilityIcon fontSize='small' />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
        />

        <Box sx={styles.savePasswordWrapper}>
          <FormControlLabel
            control={
              <Checkbox
                checked={savePasswordChecked}
                onChange={(e) => setSavePasswordChecked(e.target.checked)}
                sx={styles.checkbox}
              />
            }
            label={t('login.savePassword')}
            sx={styles.savePasswordLabel}
          />
        </Box>

        <Button type='submit' disabled={isSubmitting} sx={styles.submitButton}>
          {t('login.title')}
        </Button>
      </form>
    </Box>
  );
};

export default GolaLoginForm;
